import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../config';
import AdminLayout from './AdminLayout';

const API_URL = API_BASE_URL
const PageAdminCourseMap = () => {
  // State management
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    course_title: '',
    course_data: '',
    course_item: '',
    course_image: '',
    tense: '',
    aspect: '',
    modifier: '',
    hide: false
  });
  const token = localStorage.getItem('jstoken') || ""

  // Fetch all courses on component mount
  useEffect(() => {
    fetchCourses();
  }, []);

  // Fetch courses from API
  const fetchCourses = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/course`);
      if (response.data.courses) {
        setCourses(response.data.courses);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch courses',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error fetching courses',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'difficulty_level' ? parseInt(value, 10) : value
    });
  };

  // Handle example changes
  const handleExampleChange = (index, field, value) => {
    const updatedExamples = [...formData.examples];
    updatedExamples[index] = {
      ...updatedExamples[index],
      [field]: value
    };
    setFormData({ ...formData, examples: updatedExamples });
  };

  // Add an empty example
  const addExample = () => {
    setFormData({
      ...formData,
      examples: [
        ...formData.examples,
        { example_text: '', sentence_type: 'affirmative' }
      ]
    });
  };

  // Remove an example
  const removeExample = (index) => {
    const updatedExamples = [...formData.examples];
    updatedExamples.splice(index, 1);
    setFormData({ ...formData, examples: updatedExamples });
  };

  // Handle quiz changes
  const handleQuizChange = (index, field, value) => {
    const updatedQuizzes = [...formData.quizzes];
    if (field === 'options') {
      // Parse options as array
      value = value.split(',').map(option => option.trim());
    }
    updatedQuizzes[index] = {
      ...updatedQuizzes[index],
      [field]: value
    };
    setFormData({ ...formData, quizzes: updatedQuizzes });
  };

  // Add an empty quiz
  const addQuiz = () => {
    setFormData({
      ...formData,
      quizzes: [
        ...formData.quizzes,
        { question: '', options: ['', '', '', ''], correct_answer: '', question_type: 'mcq' }
      ]
    });
  };

  // Remove a quiz
  const removeQuiz = (index) => {
    const updatedQuizzes = [...formData.quizzes];
    updatedQuizzes.splice(index, 1);
    setFormData({ ...formData, quizzes: updatedQuizzes });
  };

  // Handle course selection
  const handleSelectCourse = (course) => {
    setSelectedCourse(course);
    setIsEditing(false);
    setIsCreating(false);
  };

  // Edit selected course
  const handleEditCourse = () => {
    if (!selectedCourse) return;

    // Transform the existing course data to match our form structure
    const courseToEdit = {
      course_title: selectedCourse.course_title || '',
      course_data: selectedCourse.course_data || '',
      course_item: selectedCourse.course_item || '',
      course_image: selectedCourse.course_image || '',
      tense: selectedCourse.tense || '',
      aspect: selectedCourse.aspect || '',
      modifier: selectedCourse.modifier || '',
      hide: selectedCourse.hide || false
    };

    setFormData(courseToEdit);
    setIsEditing(true);
    setIsCreating(false);
  };

  // Create new course
  const handleNewCourse = () => {
    setFormData({
      course_title: '',
      course_data: '',
      course_item: '',
      course_image: '',
      tense: '',
      aspect: '',
      modifier: '',
      hide: false
    });
    setSelectedCourse(null);
    setIsEditing(false);
    setIsCreating(true);
  };

  // Submit form - handles both create and update
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Prepare the payload
    const payload = {
      course_title: formData.course_title,
      course_data: formData.course_data,
      course_item: formData.course_item,
      course_image: formData.course_image,
      tense: formData.tense,
      aspect: formData.aspect,
      modifier: formData.modifier,
      hide: formData.hide
    };

    try {
      let response;
      if (isCreating) {
        // Creating a new course
        const response = await axios.post(`${API_URL}/course`, payload, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
            }
          }
        );
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Course created successfully',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
      } else if (isEditing) {
        // Updating an existing course
        response = await axios.put(`${API_URL}/course/${selectedCourse.course_id}`, payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token
            }
          }
        );
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Course updated successfully',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
      }

      // Refresh courses list
      fetchCourses();
      setIsEditing(false);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving course:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to save course',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Delete course
  const handleDeleteCourse = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed || !selectedCourse) return;

    setIsLoading(true);
    try {
      await axios.delete(`${API_URL}/course/${selectedCourse.course_id}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        }
      });
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Course deleted successfully',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      setSelectedCourse(null);
      fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete course',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel editing/creating
  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
  };

  return (
    <AdminLayout title="Admin Course Management">
      <div className="container mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-6">Course Management</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left panel - Course List */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Courses</h2>
              <button
                onClick={handleNewCourse}
                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
              >
                New Course
              </button>
            </div>

            {isLoading && <p className="text-center py-4">Loading...</p>}

            <div className="max-h-[100vh] overflow-y-auto">
              {courses.map(course => (
                <div
                  key={course.course_id}
                  className={`p-3 mb-2 border rounded cursor-pointer hover:bg-gray-50 ${selectedCourse?.course_id === course.course_id ? 'bg-blue-50 border-blue-300' : ''}`}
                  onClick={() => handleSelectCourse(course)}
                >
                  <div className="font-medium">{course.course_title}</div>
                  <div className="text-sm text-gray-600 truncate">{course.course_item}</div>
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">{course.course_id}</span>
                    <span className="text-xs bg-blue-100 px-2 py-0.5 rounded">{course.hide ? 'Hidden' : 'Visible'}</span>
                  </div>
                </div>
              ))}

              {!isLoading && courses.length === 0 && (
                <p className="text-center py-4 text-gray-500">No courses found</p>
              )}
            </div>
          </div>

          {/* Middle panel - Course Details */}
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-semibold mb-4">Course Details</h2>

            {selectedCourse ? (
              <div>
                <h3 className="text-xl font-bold mb-2">{selectedCourse.course_title}</h3>
                <p className="mb-3">{selectedCourse.course_data}</p>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-xs text-gray-500">Course ID</span>
                    <p>{selectedCourse.course_id || 'Not specified'}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-xs text-gray-500">Course Item</span>
                    <p>{selectedCourse.course_item || 'Not specified'}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-xs text-gray-500">Tense</span>
                    <p>{selectedCourse.tense || 'Not specified'}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-xs text-gray-500">Status</span>
                    <p>{selectedCourse.hide ? 'Hidden' : 'Visible'}</p>
                  </div>
                </div>

                {selectedCourse.aspect && (
                  <div className="mb-4">
                    <h4 className="font-semibold">Aspect</h4>
                    <p className="text-sm bg-gray-50 p-2 rounded border">{selectedCourse.aspect}</p>
                  </div>
                )}

                {selectedCourse.modifier && (
                  <div className="mb-4">
                    <h4 className="font-semibold">Modifier</h4>
                    <p className="text-sm bg-gray-50 p-2 rounded border">{selectedCourse.modifier}</p>
                  </div>
                )}

                {selectedCourse.course_image && (
                  <div className="mb-4">
                    <h4 className="font-semibold">Course Image</h4>
                    <img src={selectedCourse.course_image} alt="Course" className="max-w-32 max-h-24 rounded border" />
                  </div>
                )}

                <div className="flex space-x-2 mt-6">
                  <button
                    onClick={handleEditCourse}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleDeleteCourse}
                    className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ) : !isCreating && !isEditing ? (
              <p className="text-center py-10 text-gray-500">Select a course to view details or create a new one</p>
            ) : null}
          </div>

          {/* Right panel - Edit/Create Form */}
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-semibold mb-4">
              {isCreating ? 'Create New Course' : isEditing ? 'Edit Course' : 'Form'}
            </h2>

            {(isEditing || isCreating) && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Course Title*</label>
                  <input
                    type="text"
                    name="course_title"
                    value={formData.course_title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Course Data*</label>
                  <textarea
                    name="course_data"
                    value={formData.course_data}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
                    rows="3"
                    required
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Course Item*</label>
                  <input
                    type="text"
                    name="course_item"
                    value={formData.course_item}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Course Image URL</label>
                  <input
                    type="text"
                    name="course_image"
                    value={formData.course_image}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Tense</label>
                    <select
                      name="tense"
                      value={formData.tense}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
                    >
                      <option value="">Select Tense</option>
                      <option value="PRES">Present</option>
                      <option value="PAST">Past</option>
                      <option value="FUT">Future</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Aspect</label>
                    <select
                      name="aspect"
                      value={formData.aspect}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
                    >
                      <option value="">Select Aspect</option>
                      <option value="SIMP">Simple</option>
                      <option value="CONT">Continuous</option>
                      <option value="PERF">Perfect</option>
                      <option value="PERFCONT">Perfect Continuous</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Modifier</label>
                    <select
                      name="modifier"
                      value={formData.modifier}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
                    >
                      <option value="">None</option>
                      <option value="M">Modal</option>
                      <option value="PASV">Passive</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="hide"
                      checked={formData.hide}
                      onChange={(e) => setFormData({...formData, hide: e.target.checked})}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium">Hide Course</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PageAdminCourseMap;